import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { SEO } from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowRight, ArrowLeftRight, Clock, ExternalLink, Loader2, Newspaper, Search } from "lucide-react";

const BASE_URL = "https://allwhufc.com";
const PAGE_SIZE = 5;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

// Compact DD/MM/YY for the historical-transfers table so the column fits.
function formatDateShort(iso, raw) {
  if (iso) {
    try {
      return new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    } catch {}
  }
  // Fall back to a year extracted from the raw text, otherwise raw itself
  if (raw) {
    const m = String(raw).match(/(19|20)\d{2}/);
    if (m) return m[0].slice(-2);
    return raw;
  }
  return "";
}

// Compact fee string for the table: £25,500,000 → £25.5M, £500,000 → £500K,
// "Not Known"/"Undisclosed" → "N/A", "Free transfer" → "Free", keep "Loan".
function formatFeeShort(fee) {
  if (!fee) return "N/A";
  const s = String(fee).trim();
  const low = s.toLowerCase();
  if (!s || low === "not known" || low === "undisclosed" || low === "n/a" || low === "—" || low === "-") return "N/A";
  if (low.startsWith("free")) return "Free";
  if (low.startsWith("loan") || low === "loaned") return "Loan";
  // Extract first numeric value and optional £ prefix
  const m = s.replace(/[, ]/g, "").match(/(£)?(\d+(?:\.\d+)?)/);
  if (!m) return s;
  const prefix = m[1] || "";
  const n = parseFloat(m[2]);
  if (Number.isNaN(n)) return s;
  if (n >= 1_000_000) {
    const v = n / 1_000_000;
    return `${prefix}${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}M`;
  }
  if (n >= 1_000) {
    const v = n / 1_000;
    return `${prefix}${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}K`;
  }
  return s;
}

// "West Ham United" → "WHU" for the From/To column (keep other clubs as-is).
function shortClub(name) {
  if (!name) return "";
  if (/west\s*ham(\s+united)?/i.test(name)) return "WHU";
  return name;
}

function transferLink(item) {
  // Manual transfer-news has slug routing to /transfers/news/:slug.
  // Scraped news has slug routing to /news/:slug (which already has its own
  // SEO shell).
  if (item.manual) return `/transfers/news/${item.slug}`;
  if (item.news_slug) return `/news/${item.news_slug}`;
  return item.source_url || "#";
}

export default function TransfersPage() {
  // ---------- Transfer news ----------
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsHasMore, setNewsHasMore] = useState(true);
  const [newsPage, setNewsPage] = useState(0);

  const loadNews = async (page) => {
    setNewsLoading(true);
    try {
      const res = await axios.get(`${API}/transfers/news`, {
        params: { limit: PAGE_SIZE, skip: page * PAGE_SIZE },
      });
      setNews((prev) => (page === 0 ? res.data.items : [...prev, ...res.data.items]));
      setNewsHasMore(res.data.has_more);
    } catch {
      // best-effort: leave the existing list intact
    } finally {
      setNewsLoading(false);
    }
  };

  useEffect(() => {
    loadNews(0);
  }, []);

  // ---------- Historical transfers ----------
  const [team, setTeam] = useState("men");
  const [direction, setDirection] = useState("");
  const [loanFilter, setLoanFilter] = useState(""); // "", "perm", "loan"
  const [season, setSeason] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingT, setLoadingT] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    axios.get(`${API}/transfers/seasons`).then((r) => setSeasons(r.data.seasons || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setLoadingT(true);
    const params = { team, limit: 500 };
    if (direction) params.direction = direction;
    if (season) params.season = season;
    if (loanFilter === "loan") params.loan = true;
    if (loanFilter === "perm") params.loan = false;
    if (q.trim()) params.q = q.trim();
    axios
      .get(`${API}/transfers`, { params })
      .then((r) => {
        setTransfers(r.data.transfers || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => {
        setTransfers([]);
        setTotal(0);
      })
      .finally(() => setLoadingT(false));
  }, [team, direction, loanFilter, season, q]);

  // Group transfers by season for readable display
  const grouped = useMemo(() => {
    const m = new Map();
    for (const t of transfers) {
      const key = t.season || "Other";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(t);
    }
    return Array.from(m.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [transfers]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "West Ham United Transfers",
    description:
      "Complete record of West Ham United transfers — men's team from 2000 to present, women's team from 2018 to present — plus the latest transfer news.",
    url: `${BASE_URL}/transfers`,
    isPartOf: { "@type": "WebSite", name: "All WHUFC", url: BASE_URL },
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="transfers-page">
      <SEO
        title="West Ham Transfers"
        path="/transfers"
        description="West Ham United transfers — latest transfer news plus a complete historical record. Men's team from 2000-present, women's from 2018-present."
        jsonLd={jsonLd}
      />
      <Navbar />

      <header className="bg-[#7C2C3B] text-white py-12 mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight"
            style={{ fontFamily: "Barlow Condensed" }}
            data-testid="transfers-h1"
          >
            WEST HAM TRANSFERS
          </h1>
          <p className="mt-3 text-lg text-[#F8D742]/90 max-w-3xl">
            The latest West Ham United transfer news, plus a complete chronological record of every player in and out of the club.
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 space-y-12">
        {/* ----------- LATEST TRANSFER NEWS ----------- */}
        <section data-testid="transfer-news-section">
          <div className="flex items-center gap-3 mb-4">
            <Newspaper className="w-6 h-6 text-[#7C2C3B]" />
            <h2
              className="text-2xl md:text-3xl font-bold text-[#0F172A]"
              style={{ fontFamily: "Barlow Condensed" }}
              data-testid="transfer-news-h2"
            >
              LATEST WEST HAM TRANSFER NEWS
            </h2>
          </div>

          {news.length === 0 && !newsLoading && (
            <div className="bg-white border border-slate-200 rounded-lg p-6 text-slate-500 text-sm">
              No transfer news yet. Check back soon — admins post manually here and our news scraper auto-classifies transfer stories from major sources.
            </div>
          )}

          <div className="space-y-3" data-testid="transfer-news-list">
            {news.map((item) => {
              const to = transferLink(item);
              const isExternal = !item.manual && !item.news_slug && to.startsWith("http");
              const card = (
                <article className="bg-white border border-slate-200 rounded-lg p-5 hover:border-[#2BAEE5] hover:shadow-md transition-all">
                  <div className="flex items-start gap-4">
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt=""
                        className="w-24 h-24 object-cover rounded-md flex-shrink-0 hidden sm:block"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-1">
                        <span className="font-semibold text-[#7C2C3B]">{item.source}</span>
                        {item.published_at && (
                          <>
                            <span>·</span>
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {formatDate(item.published_at)}
                            </span>
                          </>
                        )}
                        {item.manual && (
                          <span className="ml-1 px-1.5 py-0.5 rounded bg-[#F8D742]/30 text-[#7C2C3B] font-semibold tracking-wide text-[10px]">
                            EDITORIAL
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg font-semibold text-[#0F172A] leading-tight mb-1 line-clamp-2">
                        {item.title}
                      </h3>
                      {item.body && (
                        <p className="text-sm text-slate-600 line-clamp-2">{item.body}</p>
                      )}
                      <div className="mt-2 inline-flex items-center gap-1 text-sm text-[#2BAEE5] font-medium">
                        Read more{" "}
                        {isExternal ? <ExternalLink className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
                      </div>
                    </div>
                  </div>
                </article>
              );
              return isExternal ? (
                <a
                  key={item.id || to}
                  href={to}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`transfer-news-card-${item.slug || item.id}`}
                >
                  {card}
                </a>
              ) : (
                <Link
                  key={item.id || to}
                  to={to}
                  data-testid={`transfer-news-card-${item.slug || item.id}`}
                >
                  {card}
                </Link>
              );
            })}
          </div>

          <div className="mt-4 flex justify-center">
            {newsHasMore ? (
              <button
                type="button"
                onClick={() => {
                  const next = newsPage + 1;
                  setNewsPage(next);
                  loadNews(next);
                }}
                disabled={newsLoading}
                data-testid="transfer-news-load-more"
                className="inline-flex items-center gap-2 bg-[#7C2C3B] hover:bg-[#63232F] disabled:opacity-60 text-white px-5 py-2.5 rounded-lg font-semibold transition-colors"
              >
                {newsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Load more news
              </button>
            ) : news.length > 0 ? (
              <span className="text-xs text-slate-400">No more headlines</span>
            ) : null}
          </div>
        </section>

        {/* ----------- HISTORICAL TRANSFERS ----------- */}
        <section data-testid="historical-transfers-section">
          <div className="flex items-center gap-3 mb-4">
            <ArrowLeftRight className="w-6 h-6 text-[#7C2C3B]" />
            <h2
              className="text-2xl md:text-3xl font-bold text-[#0F172A]"
              style={{ fontFamily: "Barlow Condensed" }}
              data-testid="historical-transfers-h2"
            >
              HISTORICAL TRANSFERS
            </h2>
            <span className="text-xs text-slate-500">
              Men's 2000-present · Women's 2018-present · Source: Wikipedia
            </span>
          </div>

          {/* Filters */}
          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-1" role="tablist">
              {[
                { k: "men", label: "Men's" },
                { k: "women", label: "Women's" },
              ].map(({ k, label }) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`transfers-team-${k}`}
                  onClick={() => setTeam(k)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                    team === k
                      ? "bg-[#7C2C3B] text-white border-[#7C2C3B]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#7C2C3B] hover:text-[#7C2C3B]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1">
              {[
                { k: "", label: "All" },
                { k: "in", label: "In" },
                { k: "out", label: "Out" },
              ].map(({ k, label }) => (
                <button
                  key={label}
                  type="button"
                  data-testid={`transfers-direction-${k || "all"}`}
                  onClick={() => setDirection(k)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                    direction === k
                      ? "bg-[#2BAEE5] text-white border-[#2BAEE5]"
                      : "bg-white text-slate-600 border-slate-200 hover:border-[#2BAEE5] hover:text-[#2BAEE5]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1">
              {[
                { k: "", label: "All deals" },
                { k: "perm", label: "Permanent" },
                { k: "loan", label: "Loans" },
              ].map(({ k, label }) => (
                <button
                  key={label}
                  type="button"
                  data-testid={`transfers-type-${k || "all"}`}
                  onClick={() => setLoanFilter(k)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                    loanFilter === k
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-white text-slate-600 border-slate-200 hover:border-amber-500 hover:text-amber-600"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <select
              value={season}
              onChange={(e) => setSeason(e.target.value)}
              data-testid="transfers-season-select"
              className="text-sm border border-slate-200 rounded-md px-3 py-1.5 bg-white text-slate-700"
            >
              <option value="">All seasons</option>
              {seasons.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <div className="flex-1 min-w-[180px] flex items-center gap-2 border border-slate-200 rounded-md px-3 py-1.5 bg-white">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search player or club"
                data-testid="transfers-search-input"
                className="flex-1 text-sm bg-transparent outline-none placeholder:text-slate-400"
              />
            </div>

            <span className="text-xs text-slate-500 ml-auto">{total} transfers</span>
          </div>

          {loadingT ? (
            <div className="flex items-center justify-center py-12 text-slate-400 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading transfers…
            </div>
          ) : transfers.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg p-6 text-slate-500 text-sm">
              No transfers match the current filters.
            </div>
          ) : (
            <div className="space-y-6" data-testid="transfers-list">
              {grouped.map(([s, items]) => (
                <div key={s} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#7C2C3B]" style={{ fontFamily: "Barlow Condensed" }}>
                      {s} season
                    </h3>
                    <span className="text-xs text-slate-500">{items.length} entries</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Player</th>
                          <th className="text-left px-3 py-2 font-medium">Pos.</th>
                          <th className="text-left px-3 py-2 font-medium">Type</th>
                          <th className="text-left px-3 py-2 font-medium">From → To</th>
                          <th className="text-left px-3 py-2 font-medium">Fee</th>
                          <th className="text-left px-3 py-2 font-medium">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((t, i) => (
                          <tr
                            key={t.id || `${t.player}-${i}`}
                            className="border-t border-slate-100 hover:bg-slate-50"
                            data-testid={`transfer-row-${t.id || i}`}
                          >
                            <td className="px-3 py-2 font-medium text-[#0F172A]">{t.player}</td>
                            <td className="px-3 py-2 text-slate-500">{t.position || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              <span
                                className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${
                                  t.direction === "in"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-rose-100 text-rose-700"
                                }`}
                              >
                                {t.direction}
                              </span>
                              {t.loan && (
                                <span
                                  className="ml-1 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase bg-amber-100 text-amber-700"
                                  data-testid={`loan-badge-${t.id}`}
                                  title="Loan deal — not a permanent transfer"
                                >
                                  Loan
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-slate-700">
                              <span className="text-slate-500">{shortClub(t.from_club)}</span>{" "}
                              <ArrowRight className="inline w-3 h-3 mx-1 text-slate-400" />{" "}
                              <span className="font-medium">{shortClub(t.to_club)}</span>
                            </td>
                            <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{formatFeeShort(t.fee)}</td>
                            <td className="px-3 py-2 text-slate-500 whitespace-nowrap">
                              {formatDateShort(t.date, t.date_raw)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Crawler-friendly hidden long-form summary for additional context */}
        <section className="prose max-w-none text-slate-600 text-sm">
          <h2 className="text-lg font-bold text-[#0F172A] mb-2" style={{ fontFamily: "Barlow Condensed" }}>
            ABOUT WEST HAM TRANSFERS
          </h2>
          <p>
            This page tracks every transfer in and out of West Ham United Football Club. The men's record
            starts with the 2000-01 season and runs through to the current Championship campaign, while the
            women's record begins with the 2018-19 FA WSL season. Data is refreshed nightly from Wikipedia
            and supplemented by editorial coverage and live news aggregation.
          </p>
        </section>
      </main>

      <Footer />
    </div>
  );
}
